import { Box } from '@mui/material';
import { useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { INode, IPage, ITypeNode } from '../../NewFormBuilder/Context/FormBuilderContext';
import { getNodesForPage } from '../../NewFormBuilder/utils/pageUtils';
import { Image } from '../Nodes/Image';
import { Input } from '../Nodes/Input';
import { List } from '../Nodes/List';
import { Output } from '../Nodes/Output';
import { ShortText } from '../Nodes/ShortText';
import { Submit } from '../Nodes/Submit';
import { Title } from '../Nodes/Title';

const ResponsiveReactGridLayout = WidthProvider(Responsive) as any;

interface SideBySideViewProps {
  publishedNodes: INode[];
  pages: IPage[];
}

interface PageContentProps {
  nodes: INode[];
  pageWidth: number;
}

function PageContent({ nodes, pageWidth }: PageContentProps) {
  const breakpoints = useMemo(
    () => ({
      lg: 1200,
      md: 996,
      sm: 768,
      xs: 480,
      xxs: 0,
    }),
    [],
  );

  const cols = useMemo(
    () => ({
      lg: 12,
      md: 10,
      sm: 6,
      xs: 4,
      xxs: 2,
    }),
    [],
  );

  const generateResponsiveLayouts = useMemo(() => {
    if (!nodes || nodes.length === 0) {
      return { lg: [], md: [], sm: [], xs: [], xxs: [] };
    }

    const layouts: { [key: string]: any[] } = {
      lg: [],
      md: [],
      sm: [],
      xs: [],
      xxs: [],
    };

    const sortedNodes = [...nodes].sort((a, b) => {
      const yA = a.layout.y;
      const yB = b.layout.y;
      if (yA !== yB) {
        return yA - yB;
      }
      return a.layout.x - b.layout.x;
    });

    Object.keys(cols).forEach(breakpoint => {
      const numCols = cols[breakpoint as keyof typeof cols];
      const lgCols = cols.lg;

      const isMobile = breakpoint === 'xs' || breakpoint === 'xxs';

      layouts[breakpoint] = sortedNodes.map((node, index) => {
        const lgLayout = node.layout;

        if (isMobile) {
          let cumulativeY = 0;
          for (let i = 0; i < index; i++) {
            const prevNode = sortedNodes[i];
            cumulativeY += prevNode.layout.h;
          }

          return {
            ...lgLayout,
            i: node.id,
            x: 0,
            y: cumulativeY,
            w: numCols,
          };
        }

        const ratio = numCols / lgCols;
        const newX = Math.floor(lgLayout.x * ratio);
        const newW = Math.max(1, Math.floor(lgLayout.w * ratio));

        return {
          ...lgLayout,
          i: node.id,
          x: Math.min(newX, numCols - newW),
          w: newW,
        };
      });
    });

    return layouts;
  }, [nodes, cols]);

  return (
    <Box 
      sx={{ 
        width: {
          xs: '100%',
          md: `${pageWidth}px`,
        },
        flexShrink: 0,
        padding: {
          xs: 0,
          md: 0,
        },
        margin: {
          xs: 0,
          md: 0,
        },
      }}
    >
      <Box
        sx={{
          '& .react-grid-layout': {
            paddingLeft: {
              xs: 0,
              md: '10px',
            },
            paddingRight: {
              xs: 0,
              md: '10px',
            },
          },
        }}
      >
        <ResponsiveReactGridLayout
          className="layout"
          layouts={generateResponsiveLayouts}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={30}
          isDraggable={false}
          isResizable={false}
          verticalCompact={false}
          margin={[10, 10]}
          containerPadding={[10, 10]}
        >
        {nodes.map(node => (
          <div
            key={node.id}
            style={{
              border: `${node.data.styles.borderWeight}px solid ${node.data.styles.borderColor}`,
              borderRadius: node.data.styles.borderRadius,
              backgroundColor: node.data.styles.backgroundColor,
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                padding: `${
                  node.data.styles.padding !== undefined &&
                  node.data.styles.padding !== null
                    ? node.data.styles.padding
                    : 16
                }px`,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: node.data.styles.verticalAlign,
              }}
            >
              {node.type === ITypeNode.TITLE && <Title node={node} />}

              {node.type === ITypeNode.SHORT_TEXT && (
                <ShortText node={node} />
              )}

              {node.type === ITypeNode.IMAGE && <Image node={node} />}

              {node.type === ITypeNode.LIST && <List node={node} />}

              {node.type === ITypeNode.INPUT && <Input node={node} />}

              {node.type === ITypeNode.OUTPUT && <Output node={node} />}

              {node.type === ITypeNode.SUBMIT && <Submit node={node} />}
            </div>
          </div>
        ))}
        </ResponsiveReactGridLayout>
      </Box>
    </Box>
  );
}

export function SideBySideView({
  publishedNodes,
  pages,
}: SideBySideViewProps) {
  const pageNodes = useMemo(() => {
    if (!pages || pages.length === 0) {
      return [];
    }

    return pages.map(page => ({
      page,
      nodes: getNodesForPage(page, publishedNodes || []),
    }));
  }, [pages, publishedNodes]);

  // Calculate page width: (1300px - gap) / number of pages
  // Gap is 32px between pages, so total gap = (pages.length - 1) * 32
  const containerWidth = 1300;
  const gap = 32;
  const totalGap = pageNodes.length > 1 ? (pageNodes.length - 1) * gap : 0;
  const pageWidth = pageNodes.length > 0 
    ? Math.floor((containerWidth - totalGap) / pageNodes.length)
    : containerWidth;

  if (pageNodes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {
          xs: 'column',
          md: 'row',
        },
        gap: {
          xs: 0,
          md: `${gap}px`,
        },
        alignItems: 'flex-start',
        width: {
          xs: '100%',
          md: `${containerWidth}px`,
        },
        maxWidth: {
          xs: '100%',
          md: `${containerWidth}px`,
        },
        padding: {
          xs: 0,
          md: 0,
        },
        margin: {
          xs: 0,
          md: 0,
        },
      }}
    >
      {pageNodes.map(({ page, nodes }, index) => (
        <PageContent 
          key={page.id} 
          nodes={nodes} 
          pageWidth={pageWidth}
        />
      ))}
    </Box>
  );
}

